import React, { useState, useEffect, useRef } from 'react';
import {
  Shield, Zap, TrendingUp, Layers, Users, Globe,
  BarChart3, Target, CheckCircle, ArrowRight,
  Smartphone, Menu, X, ChevronRight, Building,
  Home, Activity, Database, Cpu, Scan, FileText, Network,
  Calculator, Terminal, Hammer, MessageSquare, Download,
  MapPin, Mail, ExternalLink, Search, Map as MapIcon, Loader2, AlertTriangle,
  Camera, User, Calendar, BadgeCheck, Send, Clock, FileCheck, DollarSign, Wrench, RefreshCw,
  BookOpen, ChevronLeft, Share2, Tag, Lock, Server, File,
  LayoutDashboard, Inbox, Settings, Bell, LogOut, MoreHorizontal, Filter, Eye, Image as ImageIcon, Flame,
  History, CheckSquare, AlertOctagon, Snowflake, Sun, CloudRain, Wind, Briefcase, HardHat, UploadCloud,
  Sparkles, ArrowLeftRight
} from 'lucide-react';
import { createNotificationsClient, ConnectionState, EventType } from './sseClient.js';

// --- API Helper ---
const api = {
  async post(endpoint, data) {
    const response = await fetch(`/api/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return response.json();
  },
  async get(endpoint) {
    const response = await fetch(`/api/${endpoint}`);
    return response.json();
  },
};

const FirebreakWebsite = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [selectedPost, setSelectedPost] = useState(null);
  const [trustTab, setTrustTab] = useState('security');
  const [monitorSessionId, setMonitorSessionId] = useState(() => {
    if (typeof window === 'undefined') return '';
    const raw = new URLSearchParams(window.location.search).get('sessionId') || '';
    const urlSession = /^[a-zA-Z0-9_-]{1,128}$/.test(raw) ? raw : '';
    return urlSession || localStorage.getItem('home_monitor_session') || '';
  });

  // Handle scroll for navbar transparency
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top and close menu when view changes
  useEffect(() => {
    window.scrollTo(0, 0);
    setMobileMenuOpen(false);
  }, [currentView, selectedPost, trustTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (monitorSessionId) {
      localStorage.setItem('home_monitor_session', monitorSessionId);
    } else {
      localStorage.removeItem('home_monitor_session');
    }
  }, [monitorSessionId]);

  // Minimal hook to mirror queue-first SSE contract
  const useQueueNotifications = (sessionId) => {
    const clientRef = useRef(null);
    const [state, setState] = useState(ConnectionState.DISCONNECTED);
    const [progress, setProgress] = useState({
      zones: {},
      totalZones: 0,
      completedZones: 0,
      failedZones: 0,
      progress: 0
    });
    const [events, setEvents] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
      clientRef.current = createNotificationsClient();
      return () => {
        clientRef.current?.destroy?.();
      };
    }, []);

    useEffect(() => {
      const client = clientRef.current;
      if (!client || !sessionId) return;

      const unsubscribes = [
        client.on('state_change', ({ newState }) => {
          setState(newState);
        }),
        client.on(EventType.PROGRESS, (p) => setProgress(p)),
        client.on(EventType.ZONE_COMPLETE, (evt) => setEvents((prev) => [...prev, evt])),
        client.on(EventType.ZONE_ERROR, (evt) => setEvents((prev) => [...prev, evt])),
        client.on(EventType.REPORT_FINAL, (evt) => setEvents((prev) => [...prev, evt])),
        client.on('error', ({ error: errMsg }) => setError(new Error(errMsg)))
      ];

      client.connect(sessionId, 'home-app', sessionId).catch((err) => setError(err));

      return () => {
        unsubscribes.forEach((fn) => fn && fn());
        client.disconnect();
      };
    }, [sessionId]);

    const retry = () => {
      const client = clientRef.current;
      if (client && sessionId) {
        client.connect(sessionId, 'home-app', sessionId).catch((err) => setError(err));
      }
    };

    return {
      state,
      connected: state === ConnectionState.CONNECTED || state === ConnectionState.FALLBACK_POLLING,
      connecting: state === ConnectionState.CONNECTING,
      progress,
      events,
      error,
      retry,
      disconnect: () => clientRef.current?.disconnect?.()
    };
  };

  // --- Styles ---
  const CustomStyles = () => (
    <style>{`
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideInRight {
        from { opacity: 0; transform: translateX(20px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes spin-slow {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes pulse-glow {
        0%, 100% { opacity: 0.5; box-shadow: 0 0 10px rgba(245,158,11,0.2); }
        50% { opacity: 1; box-shadow: 0 0 25px rgba(245,158,11,0.6); }
      }
      @keyframes scan-line {
        0% { top: 0%; opacity: 0; }
        10% { opacity: 1; }
        90% { opacity: 1; }
        100% { top: 100%; opacity: 0; }
      }
      .animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
      .animate-slideInRight { animation: slideInRight 0.5s ease-out forwards; }
      .animate-spin-slow { animation: spin-slow 12s linear infinite; }
      .animate-pulse-glow { animation: pulse-glow 3s infinite; }
      .animate-scan-line { animation: scan-line 2s linear infinite; }

      .dashboard-scroll::-webkit-scrollbar { width: 6px; }
      .dashboard-scroll::-webkit-scrollbar-track { background: #1e293b; }
      .dashboard-scroll::-webkit-scrollbar-thumb { background: #475569; border-radius: 3px; }
    `}</style>
  );

  // --- Data & State for Cross-Phase Logic ---
  const [jobStatus, setJobStatus] = useState('assigned');

  // --- Shared Components ---
  const Navigation = () => (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled || mobileMenuOpen ? 'bg-slate-950/90 backdrop-blur-md border-b border-slate-800 py-4' : 'bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <div className="flex items-center gap-2 cursor-pointer group" onClick={() => { setCurrentView('home'); setSelectedPost(null); }}>
          <div className="bg-amber-500 p-2 rounded-lg group-hover:bg-amber-400 transition-colors"><Shield className="w-6 h-6 text-slate-950" /></div>
          <span className="text-xl font-bold tracking-wider text-white group-hover:text-slate-200 transition-colors">FIREBREAK</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-300">
          {['Technology', 'Solutions', 'Marketplace', 'Company'].map((item) => (
            <button key={item} onClick={() => { setCurrentView(item.toLowerCase()); setSelectedPost(null); }} className={`relative hover:text-amber-500 transition-colors py-2 ${currentView === item.toLowerCase() ? 'text-white' : ''}`}>{item}</button>
          ))}
          <div className="h-6 w-px bg-slate-800"></div>
          <button onClick={() => setCurrentView('dashboard')} className="text-slate-300 hover:text-white font-medium transition-colors">Log In</button>
          <button onClick={() => setCurrentView('contact')} className="bg-white text-slate-900 px-5 py-2.5 rounded-full font-bold hover:bg-amber-500 hover:text-white hover:scale-105 transition-all active:scale-95">Book Demo</button>
        </div>
        <button className="md:hidden text-white p-2 hover:bg-slate-800 rounded-lg transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle Menu">{mobileMenuOpen ? <X /> : <Menu />}</button>
      </div>
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[73px] bg-slate-950/95 backdrop-blur-xl z-40 flex flex-col p-6 gap-6 animate-fadeIn h-screen">
          {['Technology', 'Solutions', 'Marketplace', 'Company'].map((item) => (
            <button key={item} onClick={() => { setCurrentView(item.toLowerCase()); setSelectedPost(null); }} className="text-2xl font-bold text-slate-300 py-4 border-b border-slate-800 text-left hover:text-amber-500 transition-colors flex justify-between items-center">{item}<ChevronRight className="w-5 h-5 opacity-50" /></button>
          ))}
          <button onClick={() => setCurrentView('dashboard')} className="text-left py-4 text-slate-300 font-bold hover:text-white border-b border-slate-800">Log In</button>
          <button onClick={() => setCurrentView('contact')} className="bg-amber-500 text-white w-full py-4 rounded-xl font-bold text-lg mt-4 hover:bg-amber-400 active:scale-95 transition-all shadow-lg shadow-amber-500/20">Book Demo</button>
        </div>
      )}
    </nav>
  );

  // --- Newsletter Subscribe Component ---
  const SubscribeForm = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle');
    const [message, setMessage] = useState('');

    const handleSubscribe = async () => {
      if (!email) return;
      setStatus('loading');
      try {
        const result = await api.post('subscribe', { email });
        if (result.success) {
          setStatus('success');
          setMessage(result.message);
          setEmail('');
        } else {
          setStatus('error');
          setMessage(result.error || 'Failed to subscribe');
        }
      } catch {
        setStatus('error');
        setMessage('Network error. Please try again.');
      }
    };

    if (status === 'success') {
      return (
        <div className="flex items-center gap-2 text-emerald-500 text-sm">
          <CheckCircle size={16} /> {message}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none"
          />
          <button
            onClick={handleSubscribe}
            disabled={status === 'loading'}
            className="bg-amber-500 text-slate-900 font-bold px-4 py-2 rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            {status === 'loading' ? '...' : 'Subscribe'}
          </button>
        </div>
        {status === 'error' && <p className="text-red-500 text-xs">{message}</p>}
      </div>
    );
  };

  const Footer = () => (
    <footer className="bg-slate-950 border-t border-slate-900 py-16 text-sm">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-12">
        <div className="col-span-1 md:col-span-1">
          <div className="flex items-center gap-2 mb-6 cursor-pointer group" onClick={() => setCurrentView('home')}>
            <Shield className="w-5 h-5 text-amber-500 group-hover:rotate-12 transition-transform" />
            <span className="text-lg font-bold text-white tracking-wider">FIREBREAK</span>
          </div>
          <p className="text-slate-500 mb-6">Intelligence Before Impact. <br/>San Francisco, CA</p>
        </div>
        <div>
           <h4 className="text-white font-bold mb-4">Solutions</h4>
           <ul className="space-y-3 text-slate-500">
             <li className="hover:text-amber-500 cursor-pointer transition-colors" onClick={() => setCurrentView('solutions')}>Carriers</li>
             <li className="hover:text-amber-500 cursor-pointer transition-colors" onClick={() => setCurrentView('solutions')}>Homeowners</li>
             <li className="hover:text-amber-500 cursor-pointer transition-colors" onClick={() => setCurrentView('marketplace')}>Find a Pro</li>
           </ul>
        </div>
        <div>
           <h4 className="text-white font-bold mb-4">Company</h4>
           <ul className="space-y-3 text-slate-500">
             <li className="hover:text-amber-500 cursor-pointer transition-colors" onClick={() => setCurrentView('company')}>About Us</li>
             <li className="hover:text-amber-500 cursor-pointer transition-colors" onClick={() => setCurrentView('blog')}>Blog</li>
           </ul>
        </div>
        <div>
           <h4 className="text-white font-bold mb-4">Stay Updated</h4>
           <SubscribeForm />
        </div>
      </div>
    </footer>
  );

  const RealtimeMonitor = () => {
    const [sessionInput, setSessionInput] = useState(monitorSessionId);
    const { connected, connecting, state, progress, events, error, retry, disconnect } =
      useQueueNotifications(monitorSessionId);

    useEffect(() => {
      setSessionInput(monitorSessionId);
    }, [monitorSessionId]);

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!sessionInput) return;
      setMonitorSessionId(sessionInput.trim());
    };

    const handleClear = () => {
      disconnect();
      setMonitorSessionId('');
      setSessionInput('');
    };

    const pct = progress?.progress || 0;
    const zoneEntries = progress?.zones ? Object.entries(progress.zones) : [];
    const finalEvent = events.find((evt) => evt.eventType === EventType.REPORT_FINAL);

    return (
      <div className="fixed bottom-4 right-4 z-50 w-80 bg-slate-900/95 border border-slate-800 rounded-xl shadow-2xl backdrop-blur px-4 py-3 text-sm space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Realtime Report</p>
            <p className="text-white font-bold leading-tight">
              {state === ConnectionState.FALLBACK_POLLING
                ? 'Polling fallback'
                : connected
                  ? 'Live (SSE)'
                  : connecting
                    ? 'Connecting...'
                    : 'Idle'}
            </p>
          </div>
          <button
            className="text-xs bg-blue-600 hover:bg-blue-500 text-white font-semibold px-2 py-1 rounded transition-colors"
            onClick={retry}
          >
            Reconnect
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <input
            value={sessionInput}
            onChange={(e) => setSessionInput(e.target.value)}
            placeholder="session id"
            className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-amber-500"
          />
          <button
            type="submit"
            className="text-xs bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-2 py-1 rounded transition-colors"
          >
            Watch
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded border border-slate-700"
          >
            Clear
          </button>
        </form>

        <div>
          <div className="flex justify-between text-xs text-slate-400 mb-1">
            <span>Progress</span>
            <span>{pct}%</span>
          </div>
          <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full ${state === ConnectionState.FALLBACK_POLLING ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            Zones {progress?.completedZones || 0}/{progress?.totalZones || zoneEntries.length}
            {progress?.failedZones ? `, ${progress.failedZones} failed` : ''}
          </p>
        </div>

        {zoneEntries.length > 0 && (
          <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
            {zoneEntries.map(([zone, info]) => (
              <div key={zone} className="flex items-center justify-between text-xs text-slate-300">
                <span className="truncate">{zone}</span>
                <span
                  className={`capitalize ${
                    info.status === 'completed'
                      ? 'text-emerald-400'
                      : info.status === 'failed'
                        ? 'text-rose-400'
                        : info.status === 'processing'
                          ? 'text-amber-300'
                          : 'text-slate-400'
                  }`}
                >
                  {info.status}
                </span>
              </div>
            ))}
          </div>
        )}

        {finalEvent && (
          <a
            href={finalEvent.reportUrl || progress?.finalReportUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-emerald-400 underline"
          >
            Open final report
          </a>
        )}

        {error && <p className="text-[11px] text-rose-400">Error: {error.message}</p>}
      </div>
    );
  };

  // --- Pages ---

  const HomePage = () => (
    <div className="animate-fadeIn">
      <div className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden bg-slate-950">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px] translate-x-1/2 -translate-y-1/4 animate-pulse-glow"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-amber-600/10 rounded-full blur-[100px] -translate-x-1/2 translate-y-1/4"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/50 border border-slate-700 text-amber-500 text-xs font-bold tracking-widest uppercase mb-6 hover:bg-slate-800 transition-colors cursor-default">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>Series A Strategic Launch
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight leading-tight mb-6">
              Intelligence <br className="hidden md:block" /><span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-amber-200">Before Impact.</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-400 mb-10 max-w-2xl leading-relaxed">
              The first AI-native climate risk platform that combines multi-model consensus with street-level granularity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => setCurrentView('solutions')} className="bg-amber-500 text-slate-950 px-8 py-4 rounded-lg font-bold text-lg hover:bg-amber-400 transition-all flex items-center justify-center gap-2 hover:scale-105 active:scale-95">Assess Your Portfolio <ArrowRight size={20} /></button>
              <button onClick={() => setCurrentView('technology')} className="px-8 py-4 rounded-lg font-bold text-lg text-white border border-slate-700 hover:bg-slate-800 transition-all hover:scale-105 active:scale-95">How it Works</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const TechnologyPage = () => (
    <div className="pt-32 pb-20 bg-slate-950 min-h-screen animate-fadeIn">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h1 className="text-4xl text-white font-bold mb-6">Technology Stack</h1>
        <div className="grid md:grid-cols-3 gap-8 mt-12 text-left">
          <div className="bg-slate-900 p-8 rounded-xl border border-slate-800">
            <Globe className="w-10 h-10 text-blue-500 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">1. Ingest</h3>
            <p className="text-slate-400">Aggregating multi-source data from satellites and street view.</p>
          </div>
          <div className="bg-slate-900 p-8 rounded-xl border border-slate-800">
            <Cpu className="w-10 h-10 text-amber-500 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">2. Verify</h3>
            <p className="text-slate-400">Multi-model consensus engine checks for hallucinations.</p>
          </div>
          <div className="bg-slate-900 p-8 rounded-xl border border-slate-800">
            <Activity className="w-10 h-10 text-emerald-500 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">3. Score</h3>
            <p className="text-slate-400">Instant risk scoring with actionable mitigation steps.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const CompanyPage = () => (
    <div className="pt-32 pb-20 bg-slate-950 min-h-screen animate-fadeIn">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h1 className="text-4xl text-white font-bold mb-6">Company & Team</h1>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-16">We founded Firebreak to solve the climate risk gap.</p>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-slate-900 p-8 rounded-xl border border-slate-800">
            <div className="w-24 h-24 bg-slate-800 rounded-full mx-auto mb-4 flex items-center justify-center"><Shield className="w-8 h-8 text-slate-500" /></div>
            <h3 className="text-white font-bold">Sarah Jenkins</h3><p className="text-amber-500 text-sm">CEO</p>
          </div>
          <div className="bg-slate-900 p-8 rounded-xl border border-slate-800">
            <div className="w-24 h-24 bg-slate-800 rounded-full mx-auto mb-4 flex items-center justify-center"><Cpu className="w-8 h-8 text-slate-500" /></div>
            <h3 className="text-white font-bold">Dr. David Chen</h3><p className="text-blue-500 text-sm">CTO</p>
          </div>
          <div className="bg-slate-900 p-8 rounded-xl border border-slate-800">
            <div className="w-24 h-24 bg-slate-800 rounded-full mx-auto mb-4 flex items-center justify-center"><TrendingUp className="w-8 h-8 text-slate-500" /></div>
            <h3 className="text-white font-bold">Marcus Thorne</h3><p className="text-emerald-500 text-sm">CRO</p>
          </div>
        </div>
      </div>
    </div>
  );

  const BlogIndex = () => (
    <div className="pt-32 pb-20 bg-slate-950 min-h-screen animate-fadeIn">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h1 className="text-4xl text-white font-bold">Blog</h1>
        <p className="text-slate-400 mt-4">Latest insights from our engineering team.</p>
      </div>
    </div>
  );

  const TrustPage = () => (
    <div className="pt-32 pb-20 bg-slate-950 min-h-screen animate-fadeIn">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h1 className="text-4xl text-white font-bold">Trust Center</h1>
        <p className="text-slate-400 mt-4">SOC 2 Type II Compliant.</p>
      </div>
    </div>
  );

  const ContactPage = () => (
    <div className="pt-32 pb-20 bg-slate-950 min-h-screen animate-fadeIn">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h1 className="text-4xl text-white font-bold mb-8">Contact Us</h1>
        <ContactForm />
      </div>
    </div>
  );

  // --- Contact Form Component (Cloudflare D1) ---
  const ContactForm = () => {
    const [formStatus, setFormStatus] = useState('idle');
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setFormStatus('submitting');
      setErrorMessage('');

      try {
        const result = await api.post('contact', formData);
        if (result.success) {
          setFormStatus('success');
          setFormData({ name: '', email: '', message: '' });
        } else {
          setFormStatus('error');
          setErrorMessage(result.error || 'Failed to send message');
        }
      } catch {
        setFormStatus('error');
        setErrorMessage('Network error. Please try again.');
      }
    };

    if (formStatus === 'success') {
      return (
        <div className="bg-slate-900 p-8 rounded-xl border border-slate-800">
          <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
          <p className="text-slate-400">Our team will be in touch shortly.</p>
          <button onClick={() => setFormStatus('idle')} className="mt-6 text-amber-500 text-sm font-bold hover:text-amber-400">Send Another</button>
        </div>
      );
    }

    return (
      <div className="bg-slate-900 p-8 rounded-xl border border-slate-800 text-left">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-slate-400 text-sm font-bold mb-2 block">Name</label>
            <input
              type="text"
              required
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-amber-500 outline-none"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
          </div>
          <div>
            <label className="text-slate-400 text-sm font-bold mb-2 block">Email</label>
            <input
              type="email"
              required
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-amber-500 outline-none"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
            />
          </div>
          <div>
            <label className="text-slate-400 text-sm font-bold mb-2 block">Message</label>
            <textarea
              required
              rows={4}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-amber-500 outline-none"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
            />
          </div>
          <button
            type="submit"
            disabled={formStatus === 'submitting'}
            className="w-full bg-white text-slate-900 px-8 py-3 rounded-lg font-bold hover:bg-amber-500 hover:text-white transition-colors disabled:opacity-50"
          >
            {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
          </button>
          {formStatus === 'error' && <p className="text-red-500 text-sm text-center">{errorMessage}</p>}
        </form>
      </div>
    );
  };

  const BlogPost = ({post}) => (
    <div className="pt-32 pb-20 bg-slate-950 min-h-screen animate-fadeIn">
      <div className="max-w-3xl mx-auto px-6">
        <button onClick={() => setSelectedPost(null)} className="text-slate-400 mb-4 hover:text-white flex items-center gap-2"><ChevronLeft size={16}/> Back</button>
        <h1 className="text-4xl text-white font-bold mb-8">{post.title}</h1>
        <div className="prose prose-invert mt-8">{post.content}</div>
      </div>
    </div>
  );

  const SolutionsPage = () => {
    const [activeTab, setActiveTab] = useState('carriers');
    return (
      <div className="pt-32 pb-20 bg-slate-950 min-h-screen animate-fadeIn">
        <div className="max-w-7xl mx-auto px-6 text-center mb-12">
          <h1 className="text-4xl text-white font-bold mb-6">Solutions</h1>
          <div className="inline-flex bg-slate-900 p-1 rounded-xl border border-slate-800">
            <button onClick={() => setActiveTab('carriers')} className={`px-6 py-2 rounded-lg font-bold transition-colors ${activeTab === 'carriers' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}>Carriers</button>
            <button onClick={() => setActiveTab('homeowners')} className={`px-6 py-2 rounded-lg font-bold transition-colors ${activeTab === 'homeowners' ? 'bg-amber-500 text-slate-900' : 'text-slate-400 hover:text-white'}`}>Homeowners</button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6">
          {activeTab === 'carriers' ? (
            <div className="bg-slate-900 p-12 rounded-xl border border-slate-800 text-center">
              <div className="inline-flex items-center gap-2 text-blue-500 font-bold uppercase text-xs tracking-wider mb-4">
                <Terminal className="w-4 h-4" /> Interactive Demo
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">Test the Engine</h2>
              <div className="h-[500px]">
                <RiskScanner />
              </div>
            </div>
          ) : (
            <div className="bg-slate-900 p-12 rounded-xl border border-slate-800 text-center">
              <Smartphone className="w-16 h-16 text-amber-500 mx-auto mb-6" />
              <h2 className="text-2xl text-white font-bold mb-4">Firebreak PRO</h2>
              <p className="text-slate-400 mb-8">Interactive homeowner demo for risk mitigation.</p>
              <button className="bg-amber-500 text-slate-900 px-8 py-3 rounded-lg font-bold">Try the App</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- Risk Scanner Component (Cloudflare D1) ---
  const RiskScanner = () => {
    const [scanStatus, setScanStatus] = useState('idle');
    const [address, setAddress] = useState('');
    const [progress, setProgress] = useState(0);
    const [scanStep, setScanStep] = useState('');
    const [inspectionState, setInspectionState] = useState('none');

    const saveScanResult = async (addr, score) => {
      try {
        await api.post('scan', {
          address: addr,
          riskScore: score,
          status: score >= 70 ? 'critical' : score >= 40 ? 'moderate' : 'low',
          vegetationRisk: 'high',
          roofRisk: 'medium',
          emberZoneRisk: 'high',
          sessionId: `session-${Date.now()}`,
        });
      } catch (e) {
        console.error('Error saving scan:', e);
      }
    };

    const startScan = (e) => {
      e.preventDefault();
      if (!address) return;
      setScanStatus('scanning');

      const steps = [
        { p: 20, text: "Locating property via Satellite..." },
        { p: 45, text: "Analyzing Vegetation Density (LiDAR)..." },
        { p: 70, text: "Cross-referencing Municipal Codes..." },
        { p: 90, text: "Calculating Risk Score..." },
        { p: 100, text: "Done" }
      ];

      let currentStep = 0;
      const interval = setInterval(() => {
        if (currentStep >= steps.length) {
          clearInterval(interval);
          setScanStatus('result');
          saveScanResult(address, 82);
          return;
        }
        setProgress(steps[currentStep].p);
        setScanStep(steps[currentStep].text);
        currentStep++;
      }, 800);
    };

    const triggerInspection = () => { setInspectionState('searching'); setTimeout(() => setInspectionState('booked'), 2500); };
    const triggerOwnerContact = () => { setInspectionState('contacting'); setTimeout(() => setInspectionState('notified'), 2000); };
    const resetScan = () => { setScanStatus('idle'); setAddress(''); setProgress(0); setInspectionState('none'); };

    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-2xl h-full flex flex-col text-left">
        <div className="bg-slate-950 px-4 py-3 border-b border-slate-800 flex items-center gap-2">
          <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500"></div><div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div><div className="w-2.5 h-2.5 rounded-full bg-green-500"></div></div>
          <div className="ml-4 text-xs font-mono text-slate-500 flex-1 text-center">Firebreak Intelligence Console</div>
          <Shield className="w-4 h-4 text-slate-600" />
        </div>

        <div className="p-6 flex-1 flex flex-col justify-center">
          {scanStatus === 'idle' && (
            <form onSubmit={startScan} className="space-y-6 animate-fadeIn">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-500"><Search size={32} /></div>
                <h3 className="text-xl font-bold text-white mb-2">Live Risk Simulator</h3>
                <p className="text-slate-400 text-sm">Enter any address to see how Firebreak analyzes risk layers in real-time.</p>
              </div>
              <div className="relative">
                <MapPin className="absolute left-3 top-3.5 text-slate-500 w-5 h-5" />
                <input type="text" placeholder="e.g. 123 Skyline Dr, Los Angeles, CA" className="w-full bg-slate-950 border border-slate-800 text-white pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:border-amber-500 transition-all" value={address} onChange={(e) => setAddress(e.target.value)} />
              </div>
              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg transition-all hover:shadow-[0_0_20px_rgba(37,99,235,0.3)]">Run Assessment</button>
            </form>
          )}

          {scanStatus === 'scanning' && (
            <div className="text-center space-y-6 animate-fadeIn">
              <div className="relative w-24 h-24 mx-auto">
                <div className="absolute inset-0 border-4 border-slate-800 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-amber-500 rounded-full border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center"><Scan className="text-amber-500 w-8 h-8 animate-pulse" /></div>
              </div>
              <div><h3 className="text-lg font-bold text-white mb-1">Analyzing Property</h3><p className="text-sm text-slate-400 font-mono">{scanStep}</p></div>
              <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden"><div className="bg-amber-500 h-full transition-all duration-300 ease-out" style={{ width: `${progress}%` }}></div></div>
            </div>
          )}

          {scanStatus === 'result' && (
            <div className="animate-fadeIn h-full flex flex-col">
              <div className="flex justify-between items-start mb-6 border-b border-slate-800 pb-4">
                <div><div className="text-xs text-slate-500 uppercase font-bold mb-1">Property</div><div className="text-white font-bold text-sm">{address}</div></div>
                <div className="text-right"><div className="text-xs text-slate-500 uppercase font-bold mb-1">Risk Score</div><div className="text-3xl font-bold text-white">82<span className="text-sm text-slate-500">/100</span></div></div>
              </div>
              {inspectionState === 'none' && (
                <div className="space-y-3 mb-6 flex-1">
                  <div className="flex items-center justify-between p-2 rounded bg-red-900/20 border border-red-500/30">
                    <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /><span className="text-sm text-red-200 font-bold">Vegetation Density</span></div>
                    <span className="text-xs font-bold bg-red-500 text-white px-2 py-0.5 rounded">CRITICAL</span>
                  </div>
                  <div className="mt-4 p-3 bg-slate-800/50 rounded border border-slate-700 text-xs text-slate-400 flex gap-2"><AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />High vegetation risk detected.</div>
                </div>
              )}
              {inspectionState === 'searching' && <div className="flex-1 flex flex-col items-center justify-center text-center animate-fadeIn"><h4 className="text-white font-bold">Locating Inspector...</h4></div>}
              {inspectionState === 'booked' && <div className="flex-1 animate-fadeIn"><div className="bg-emerald-900/10 border border-emerald-500/30 rounded-lg p-4 mb-4"><h4 className="text-white font-bold mb-2">Inspector Dispatched</h4><p className="text-sm text-slate-400">Alex Rivera arriving tomorrow at 9:00 AM.</p></div></div>}

              <div className="mt-auto pt-4 border-t border-slate-800">
                {inspectionState === 'none' && (
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <button onClick={triggerInspection} className="bg-white hover:bg-slate-200 text-slate-900 font-bold py-3 rounded-lg transition-colors flex flex-col items-center justify-center gap-1 text-xs sm:text-sm"><Camera className="w-4 h-4" />Dispatch Pro</button>
                    <button onClick={triggerOwnerContact} className="bg-slate-800 hover:bg-slate-700 border border-slate-600 hover:border-amber-500 text-white font-bold py-3 rounded-lg transition-all flex flex-col items-center justify-center gap-1 text-xs sm:text-sm group"><Smartphone className="w-4 h-4 group-hover:text-amber-500 transition-colors" />Alert Owner</button>
                  </div>
                )}
                {(inspectionState === 'booked' || inspectionState === 'notified') && (
                  <button onClick={resetScan} className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 rounded-lg transition-colors">Start New Scan</button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- Dashboard Page ---
  const DashboardPage = () => {
    const [activeTab, setActiveTab] = useState('ground-truth');

    const groundTruthFeed = [
      { id: 1, addr: '105 Marina Blvd', status: jobStatus === 'completed' ? 'verified' : 'pending', riskChange: jobStatus === 'completed' ? -15 : 0, time: '10m ago', issues: ['Zone 0 Clear'] },
      { id: 2, addr: '842 Redwood Dr', status: 'critical', riskChange: 0, time: '1h ago', issues: ['Ember Zone Violation'] },
      { id: 3, addr: '445 Sunset Ave', status: 'verified', riskChange: -22, time: '2h ago', issues: ['Roof Debris Cleared'] },
    ];

    return (
      <div className="bg-slate-950 min-h-screen text-slate-200 font-sans flex animate-fadeIn">
        <div className="w-64 bg-slate-900 border-r border-slate-800 flex-shrink-0 hidden md:flex flex-col">
          <div className="p-6 flex items-center gap-2 mb-6 cursor-pointer" onClick={() => setCurrentView('home')}>
            <Shield className="w-6 h-6 text-amber-500" />
            <span className="text-lg font-bold text-white tracking-wider">PORTFOLIO</span>
          </div>
          <div className="px-4 space-y-1 flex-1">
            <button onClick={() => setActiveTab('overview')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${activeTab === 'overview' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
              <LayoutDashboard size={18} /> Overview
            </button>
            <button onClick={() => setActiveTab('ground-truth')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${activeTab === 'ground-truth' ? 'bg-amber-500 text-slate-900 font-bold' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
              <Camera size={18} /> Ground Truth
              <span className="ml-auto bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">New</span>
            </button>
            <button onClick={() => setActiveTab('map')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${activeTab === 'map' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
              <MapIcon size={18} /> Geospatial Map
            </button>
            <button onClick={() => setActiveTab('inbox')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${activeTab === 'inbox' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
              <Inbox size={18} /> Queue
            </button>
          </div>
          <div className="p-4 border-t border-slate-800">
            <button onClick={() => setCurrentView('home')} className="w-full flex items-center gap-2 text-slate-500 hover:text-white text-xs font-bold transition-colors">
              <LogOut size={14} /> Log Out
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col h-screen overflow-hidden">
          <div className="p-8 overflow-y-auto dashboard-scroll">
            <h2 className="text-2xl font-bold text-white mb-6">Carrier Dashboard</h2>

            {activeTab === 'ground-truth' && (
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-4">
                  <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-2">Live Inspection Feed</h3>
                  {groundTruthFeed.map((item) => (
                    <div key={item.id} className="bg-slate-900 border border-slate-800 p-4 rounded-xl hover:border-slate-600 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-white font-bold text-sm">{item.addr}</span>
                        <span className="text-slate-500 text-xs">{item.time}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 ${item.status === 'verified' ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-500/30' : 'bg-red-900/30 text-red-400 border border-red-500/30'}`}>
                          {item.status === 'verified' ? <BadgeCheck size={10}/> : <AlertTriangle size={10}/>}
                          {item.status === 'verified' ? 'Verified' : 'Critical'}
                        </span>
                        {item.riskChange < 0 && <span className="bg-blue-900/30 text-blue-400 border border-blue-500/30 text-[10px] font-bold px-2 py-0.5 rounded">Risk {item.riskChange}%</span>}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="lg:col-span-2">
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-white font-bold text-lg">Inspection Details #8842</h3>
                      <div className="flex gap-2">
                        <button className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-bold">Approve</button>
                        <button className="bg-slate-800 text-white px-4 py-2 rounded-lg text-xs font-bold">Review</button>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 mb-6">
                      <h4 className="text-amber-500 font-bold text-sm uppercase mb-2">Zone 0 Status</h4>
                      <div className="flex items-center justify-between">
                        <span className="text-white text-sm">Vegetation Clearance</span>
                        <span className={`font-bold text-sm ${jobStatus === 'completed' ? 'text-emerald-500' : 'text-red-500'}`}>
                          {jobStatus === 'completed' ? 'PASS' : 'FAIL'}
                        </span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm">Select a property from the feed to view detailed photo evidence and risk analysis.</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab !== 'ground-truth' && (
              <div className="text-center py-20 text-slate-400">
                <LayoutDashboard className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                <p>Dashboard view placeholder. Switch to <strong className="text-amber-500">Ground Truth</strong> tab.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // --- Marketplace Page ---
  const MarketplacePage = () => {
    const [userRole, setUserRole] = useState('contractor');
    const [activeJob, setActiveJob] = useState(null);

    const ContractorCard = ({ name, role, rating, completed, specializations }) => (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 hover:border-amber-500 transition-all group">
        <div className="flex justify-between items-start mb-4">
          <div className="flex gap-4">
            <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center text-slate-400 border border-slate-700"><User size={24} /></div>
            <div><h3 className="text-white font-bold">{name}</h3><p className="text-amber-500 text-sm flex items-center gap-1"><BadgeCheck size={12} /> Firebreak Certified</p></div>
          </div>
          <div className="text-right"><div className="text-white font-bold">{rating} ★</div><div className="text-slate-500 text-xs">{completed} Jobs</div></div>
        </div>
        <div className="mb-4">
          <div className="text-slate-400 text-sm mb-2 flex items-center gap-2"><Briefcase size={14} /> {role}</div>
          <div className="flex flex-wrap gap-2">{specializations.map((tag, i) => (<span key={i} className="text-[10px] bg-slate-800 text-slate-300 px-2 py-1 rounded border border-slate-700">{tag}</span>))}</div>
        </div>
        <button className="w-full bg-slate-800 hover:bg-white hover:text-slate-900 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2">Contact Pro</button>
      </div>
    );

    const JobDetailView = () => {
      const [verifyStep, setVerifyStep] = useState('idle');
      const handleUpload = () => {
        setVerifyStep('uploading');
        setTimeout(() => setVerifyStep('analyzing'), 1500);
        setTimeout(() => { setVerifyStep('match'); setJobStatus('completed'); }, 4500);
      };

      return (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden animate-fadeIn h-full flex flex-col">
          <div className="bg-slate-950 p-6 border-b border-slate-800 flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3 mb-1"><button onClick={() => setActiveJob(null)} className="text-slate-500 hover:text-white transition-colors"><ChevronLeft size={20}/></button><h2 className="text-xl font-bold text-white">Work Order #WO-9921</h2></div>
              <p className="text-slate-400 text-sm ml-8">105 Marina Blvd • Zone 0 Clearance</p>
            </div>
            <div className="text-right"><div className="text-xs text-slate-500 uppercase font-bold mb-1">Reward</div><div className="text-2xl font-bold text-emerald-500">$450.00</div></div>
          </div>
          <div className="flex-1 overflow-y-auto dashboard-scroll p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="text-white font-bold flex items-center gap-2"><Sparkles className="text-amber-500 w-5 h-5" /> GenAI Definition of Done</h3><span className="text-xs bg-amber-500/20 text-amber-500 px-2 py-1 rounded border border-amber-500/30">Standard 4.1: 0-5ft Clearance</span></div>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-2">
                <div className="text-xs font-bold text-red-400 uppercase flex justify-between"><span>Current State (Risk)</span><span>10:42 AM Today</span></div>
                <div className="aspect-video bg-slate-800 rounded-lg border border-red-500/30 relative overflow-hidden group">
                  <div className="absolute inset-0 flex items-center justify-center text-slate-600"><ImageIcon size={48} /></div>
                  <div className="absolute top-1/4 left-1/4 w-24 h-24 border-2 border-red-500/70 rounded-full bg-red-500/10 flex items-center justify-center"><div className="bg-red-500 text-white text-[10px] px-1 rounded">Zone 0 Breach</div></div>
                </div>
                <p className="text-xs text-slate-400">Detected: Juniper bush contacting structure foundation.</p>
              </div>
              <div className="space-y-2">
                <div className="text-xs font-bold text-emerald-400 uppercase flex justify-between"><span>Target State (Simulation)</span><span className="flex items-center gap-1"><Cpu size={10} /> AI Generated</span></div>
                <div className="aspect-video bg-slate-800 rounded-lg border-2 border-emerald-500/30 relative overflow-hidden shadow-[0_0_20px_rgba(16,185,129,0.1)]">
                  <div className="absolute inset-0 flex items-center justify-center text-slate-600 opacity-50"><ImageIcon size={48} /></div>
                  <div className="absolute inset-0 bg-emerald-900/10"></div>
                  <div className="absolute top-1/4 left-1/4 w-24 h-24 border-2 border-dashed border-emerald-500/50 rounded-full flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"><span className="text-emerald-500 text-xs font-bold">Cleared</span></div>
                </div>
                <p className="text-xs text-slate-400">Required: Removal of biomass. Installation of non-combustible hardscape.</p>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h3 className="text-white font-bold mb-4">Verification</h3>
              {verifyStep === 'idle' && (<div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-blue-500 hover:bg-slate-800 transition-all cursor-pointer" onClick={handleUpload}><div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400"><Camera size={32} /></div><h4 className="text-white font-bold">Capture & Upload</h4><button className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold">Open Camera</button></div>)}
              {verifyStep === 'uploading' && (<div className="text-center py-12"><Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" /><h4 className="text-white font-bold">Uploading Evidence...</h4></div>)}
              {verifyStep === 'analyzing' && (<div className="text-center py-8"><div className="relative w-64 h-2 bg-slate-700 rounded-full mx-auto mb-6 overflow-hidden"><div className="absolute top-0 left-0 h-full bg-amber-500 w-1/3 animate-[slideInRight_1s_infinite]"></div></div><h4 className="text-white font-bold mb-2">Comparing to Simulation</h4></div>)}
              {verifyStep === 'match' && (<div className="text-center py-6 bg-emerald-900/20 border border-emerald-500/30 rounded-lg"><div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-[0_0_20px_rgba(16,185,129,0.5)]"><CheckCircle size={32} className="text-white" /></div><h4 className="text-white font-bold text-lg">Job Verified: 99% Match</h4><div className="mt-6 flex justify-center gap-4"><span className="text-slate-400 text-xs flex items-center gap-1"><Send size={12}/> Insurer Notified</span><span className="text-slate-400 text-xs flex items-center gap-1"><DollarSign size={12}/> Payment Released</span></div></div>)}
            </div>
          </div>
        </div>
      );
    };

    const ContractorDashboard = () => (
      <div className="grid lg:grid-cols-3 gap-8 h-[600px]">
        <div className="lg:col-span-1 space-y-4 overflow-y-auto dashboard-scroll pr-2">
          <h2 className="text-xl font-bold text-white mb-4 sticky top-0 bg-slate-950 pb-2 z-10">Active Jobs</h2>
          <div className={`bg-slate-900 border rounded-xl p-4 cursor-pointer transition-all ${activeJob === 1 ? 'border-blue-500 shadow-lg shadow-blue-900/20' : 'border-slate-800 hover:border-slate-600'}`} onClick={() => setActiveJob(1)}>
            <div className="flex justify-between items-start mb-2"><span className="bg-amber-500/20 text-amber-500 text-[10px] font-bold px-2 py-0.5 rounded uppercase">Action Required</span><span className="text-slate-500 text-xs">Due 24h</span></div>
            <h3 className="text-white font-bold">Zone 0 Clearance</h3><p className="text-slate-400 text-sm mb-3">105 Marina Blvd</p>
            <div className="flex items-center justify-between border-t border-slate-800 pt-3"><span className="text-emerald-500 font-bold text-sm">$450</span></div>
          </div>
        </div>
        <div className="lg:col-span-2 h-full">
          {activeJob ? <JobDetailView /> : <div className="h-full bg-slate-900 border border-slate-800 rounded-xl flex flex-col items-center justify-center text-center p-8"><div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-6 text-slate-600"><Briefcase size={32} /></div><h3 className="text-xl font-bold text-white mb-2">Select a Job</h3><p className="text-slate-400 max-w-xs">View specific work orders, access detected risk data, and upload verification photos.</p></div>}
        </div>
      </div>
    );

    return (
      <div className="pt-32 pb-20 bg-slate-950 min-h-screen animate-fadeIn">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-6">Resilience Marketplace</h1>
            <div className="inline-flex bg-slate-900 p-1 rounded-xl border border-slate-800">
              <button onClick={() => setUserRole('homeowner')} className={`px-6 py-2 rounded-lg font-bold transition-colors ${userRole === 'homeowner' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}>Find a Pro</button>
              <button onClick={() => setUserRole('contractor')} className={`px-6 py-2 rounded-lg font-bold transition-colors ${userRole === 'contractor' ? 'bg-amber-500 text-slate-900' : 'text-slate-400 hover:text-white'}`}>Contractor Portal</button>
            </div>
          </div>
          {userRole === 'homeowner' ? (
            <>
              <div className="max-w-2xl mx-auto mb-16 relative"><Search className="absolute left-4 top-3.5 text-slate-500 w-5 h-5" /><input type="text" placeholder="Search by trade..." className="w-full bg-slate-900 border border-slate-800 rounded-full pl-12 pr-6 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors" /></div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ContractorCard name="GreenGuard Tree Svc" role="Certified Arborist" rating="4.9" completed="142" specializations={["Zone 0 Clearance", "Canopy Reduction", "Chipping"]}/>
                <ContractorCard name="SafeHome Retrofits" role="General Contractor" rating="4.8" completed="89" specializations={["Vent Mesh", "Gutter Guards", "Siding Replacement"]}/>
                <ContractorCard name="Apex Roofing" role="Roofing Specialist" rating="5.0" completed="210" specializations={["Class A Roofs", "Debris Removal"]}/>
              </div>
            </>
          ) : (<ContractorDashboard />)}
        </div>
      </div>
    );
  };

  // --- Main Render ---
  const renderView = () => {
    if (currentView === 'dashboard') return <DashboardPage />;
    if (currentView === 'marketplace') return <MarketplacePage />;
    if (selectedPost) return <BlogPost post={selectedPost} />;

    switch(currentView) {
      case 'home': return <HomePage />;
      case 'technology': return <TechnologyPage />;
      case 'solutions': return <SolutionsPage />;
      case 'company': return <CompanyPage />;
      case 'blog': return <BlogIndex />;
      case 'trust': return <TrustPage />;
      case 'contact': return <ContactPage />;
      case 'marketplace': return <MarketplacePage />;
      default: return <HomePage />;
    }
  };

  return (
    <div className="bg-slate-950 min-h-screen text-slate-200 font-sans selection:bg-amber-500/30">
      <CustomStyles />
      {currentView !== 'dashboard' && <Navigation />}
      <main className="min-h-screen relative">
        <div key={`${currentView}-${selectedPost ? selectedPost.id : 'index'}`} className="animate-fadeIn">
          {renderView()}
        </div>
        <RealtimeMonitor />
      </main>
      {currentView !== 'dashboard' && <Footer />}
    </div>
  );
};

export default FirebreakWebsite;
